import { useState } from "react";
import { CheckCircle2, Loader2 } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";

function getUtm() {
  const p = new URLSearchParams(window.location.search);
  return {
    source: p.get("utm_source") || p.get("ref") || undefined,
    medium: p.get("utm_medium") || undefined,
    campaign: p.get("utm_campaign") || undefined,
  };
}

export default function LeadForm({ inquiryType = "contact", variant = "contact" }) {
  const isBuild = inquiryType === "build-request";
  const [form, setForm] = useState({
    name: "", email: "", company: "", phone: "",
    project_type: "", budget: "", message: "",
  });
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [error, setError] = useState("");

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setState("loading");
    setError("");
    try {
      await api.post("/leads", {
        ...form,
        inquiry_type: inquiryType,
        source_url: window.location.href,
        utm: getUtm(),
      });
      setState("done");
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
      setState("error");
    }
  };

  if (state === "done") {
    return (
      <div className="card-edge p-10 text-center" data-testid="lead-success">
        <CheckCircle2 className="w-12 h-12 text-tib-mint mx-auto" strokeWidth={1.3} />
        <h3 className="h-serif text-2xl text-white mt-5">
          {isBuild ? "Build request received." : "Message received."}
        </h3>
        <p className="text-zinc-400 mt-3">
          A member of the TIB team will reach out shortly. Thank you for reaching out.
        </p>
      </div>
    );
  }

  const input = "w-full bg-black border border-zinc-800 px-4 py-3 text-white placeholder-zinc-600 focus:border-tib-amber focus:outline-none transition-colors";

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="lead-form">
      <div className="grid sm:grid-cols-2 gap-5">
        <input data-testid="lead-name" required placeholder="Full name" className={input} value={form.name} onChange={set("name")} />
        <input data-testid="lead-email" required type="email" placeholder="Email" className={input} value={form.email} onChange={set("email")} />
      </div>
      <div className="grid sm:grid-cols-2 gap-5">
        <input data-testid="lead-company" placeholder="Company" className={input} value={form.company} onChange={set("company")} />
        <input data-testid="lead-phone" placeholder="Phone (optional)" className={input} value={form.phone} onChange={set("phone")} />
      </div>
      {isBuild && (
        <div className="grid sm:grid-cols-2 gap-5">
          <select data-testid="lead-project-type" className={input} value={form.project_type} onChange={set("project_type")}>
            <option value="">Platform type…</option>
            <option value="saas">SaaS platform</option>
            <option value="marketplace">Marketplace</option>
            <option value="fintech">Fintech / payments</option>
            <option value="internal">Internal tooling</option>
            <option value="other">Other</option>
          </select>
          <select data-testid="lead-budget" className={input} value={form.budget} onChange={set("budget")}>
            <option value="">Budget range…</option>
            <option value="<50k">Under $50k</option>
            <option value="50-150k">$50k – $150k</option>
            <option value="150-500k">$150k – $500k</option>
            <option value="500k+">$500k+</option>
          </select>
        </div>
      )}
      <textarea
        data-testid="lead-message"
        required
        rows={isBuild ? 5 : 4}
        placeholder={isBuild ? "Describe the platform you want TIB to build…" : "How can we help?"}
        className={input}
        value={form.message}
        onChange={set("message")}
      />
      {state === "error" && <p className="text-red-400 text-sm" data-testid="lead-error">{error}</p>}
      <button type="submit" disabled={state === "loading"} data-testid="lead-submit" className="btn-primary w-full justify-center disabled:opacity-60">
        {state === "loading" ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
        {isBuild ? "Submit build request" : "Send message"}
      </button>
    </form>
  );
}
